<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Alert, Input } from '@sveltestrap/sveltestrap';

  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { DomainsService } from '@client/services/domains';
  import { UsersService } from '@client/services/users';

  import { userStore } from '@client/stores/user';

  import { debounce } from '@common/utils/debounce';

  import Results from '../mdm/results.svelte';

  export let error = '';
  export let initializing = false;

  // Bindable exports - parent reads these for submit/disabled logic
  export let users = '';
  export let selectedDomains = [];
  export let actionItem = null;
  export let authcode = '';

  // Internal state
  let checkingUsers = false;
  let checkingDomains = false;
  let searchingDomains = false;

  let domains = [];
  let userAssociations = [];
  let domainAssociations = [];

  let billingid = '';
  let filterText = '';
  let textareaRef = null;

  let actionItems = [
    {
      label: $format('label.ASSIGN_TO_SELECTED_DOMAINS'),
      value: 'assign'
    },
    {
      label: $format('label.UNASSIGN_FROM_SELECTED_DOMAINS'),
      value: 'unassign'
    }
  ];

  let lookupOptions = [
    {
      label: $format('label.DOMAINS'),
      value: 'domains'
    },
    {
      label: $format('label.BILLING_ID'),
      value: 'billingid'
    }
  ];

  let lookupItem = lookupOptions[0];

  if (!actionItem) actionItem = actionItems[0];

  export function reset() {
    authcode = '';
    users = '';
    billingid = '';
    filterText = '';
    actionItem = actionItems[0];
    lookupItem = lookupOptions[0];
    userAssociations = [];
    domainAssociations = [];
    selectedDomains = [];
  }

  const initialize = async () => {
    initializing = true;
    const { data } = await DomainsService.getAll({ screen: $userStore.isStaff ? 'staff' : '', perPage: 10000 });
    domains = data.map(({ domainname }) => ({ value: domainname, label: domainname }));
    initializing = false;
  };

  onMount(async () => await initialize());

  const resize = () => {
    textareaRef.style.height = 'auto';
    textareaRef.style.height = 4 + textareaRef.scrollHeight + 'px';
  };

  const checkUsers = debounce(async () => {
    checkingUsers = true;
    userAssociations = [];

    const response = await UsersService.getUsersMDM({ mdmusers: users, mdmdomains: '', authcode });

    checkingUsers = false;

    userAssociations = response;
  }, 300);

  const checkDomains = debounce(async () => {
    checkingDomains = true;
    domainAssociations = [];

    const mdmdomains = selectedDomains?.map(({ value }) => value).join('\n');
    const response = await UsersService.getUsersMDM({ mdmusers: '', mdmdomains, authcode });

    checkingDomains = false;

    domainAssociations = response;
  }, 300);

  const processPastedDomains = (newDomain) => {
    const domainname = newDomain.trim();
    const existingSelection = selectedDomains?.find(({ value }) => value === domainname);
    const existingDomain = selectedDomains?.find(({ value }) => value === domainname);

    if (domainname.length) {
      const item = {
        value: domainname,
        label: domainname,
        checked: true
      };

      if (!existingSelection) {
        selectedDomains.push(item);
      }

      if (!existingDomain) {
        domains.push(item);
      }
    }
  };

  const onDomainsPaste = (e) => {
    e.preventDefault();

    const text = e.clipboardData.getData('text');
    const items = text.split(/[\s,]+/).filter(Boolean);

    if (!items.length) {
      return;
    }

    items.forEach((item) => processPastedDomains(item.trim()));

    domains = [...domains];
    selectedDomains = [...selectedDomains];
  };

  const updateLookupOptions = ({ detail }) => {
    selectedDomains = [];
  };

  const findDomainsByBillingId = async () => {
    searchingDomains = true;

    selectedDomains = [];

    const { data } = await DomainsService.getAll({
      billingid,
      debounceBillingid: '',
      debounceQuery: '',
      page: 1,
      perPage: !billingid.length ? 1 : 1000,
      query: '',
      screen: 'staff'
    });

    searchingDomains = false;

    selectedDomains = data.map(({ domainname }) => ({ value: domainname, label: domainname }));
  };

  const onKeyUp = debounce(async () => {
    if (!billingid.length) {
      selectedDomains = [];
      return;
    }

    if (billingid.length < 5) {
      return;
    }

    await findDomainsByBillingId();
  }, 300);

  const onAddUser = (email) => {
    users = users ? `${users}\n${email}` : email;
    checkUsers();
  };

  const onAddDomain = (domain) => {
    selectedDomains = selectedDomains
      ? [...selectedDomains, { value: domain, label: domain }]
      : [{ value: domain, label: domain }];
    checkDomains();
  };

  $: if (textareaRef) {
    resize();
  }
</script>

<div class="mdm-form">
  {#if error}
    <Alert color="danger">{@html error}</Alert>
  {/if}

  <p>{$format('label.USERS')}</p>
  <Input
    rows={1}
    type="textarea"
    name="users"
    bind:inner={textareaRef}
    on:input={resize}
    bind:value={users}
    on:keyup={checkUsers}
    placeholder={$format('label.PLACEHOLDER.MDM_USERS')}
  />

  {#if users.length}
    <Results
      bind:data={userAssociations}
      {onAddUser}
      {onAddDomain}
      {selectedDomains}
      {users}
      bind:loading={checkingUsers}
    />
  {/if}

  <p>{$format('label.DOMAINS')}</p>
  <div class="domain-selector" role="button" tabindex="0">
    {#if lookupItem?.value === 'domains'}
      <span on:paste={onDomainsPaste}>
        <Select
          bind:filterText
          bind:value={selectedDomains}
          bind:items={domains}
          on:change={checkDomains}
          on:clear={checkDomains}
          multiple
          searchable
          placeholder={$format('label.PLACEHOLDER.SELECT_DOMAINS')}
        >
          <slot name="empty" slot="empty">
            <div class="empty">
              {#if searchingDomains}
                {$format('label.LOADING')}...
              {:else}
                {$format('label.NO_DOMAINS_MATCH')}
              {/if}
            </div>
          </slot>
        </Select>
      </span>
    {:else}
      <Input
        type="text"
        name="billingid"
        bind:value={billingid}
        on:keyup={onKeyUp}
        placeholder={$format('label.PLACEHOLDER.BILLING_ID')}
      />
    {/if}

    <Select
      items={lookupOptions}
      searchable={false}
      clearable={false}
      showChevron
      on:change={updateLookupOptions}
      bind:value={lookupItem}
      placeholder={$format('label.PLACEHOLDER.SELECT_ACTION')}
    />
  </div>

  {#if (selectedDomains?.length || searchingDomains) && lookupItem?.value === 'billingid'}
    <div class="selected-domains">
      <p>
        {$format('label.MATCHES')}
        {#if !searchingDomains}
          ({selectedDomains.length.toLocaleString()})
        {/if}
      </p>
      {#if searchingDomains}
        <div class="spinner">
          <Spinner show inline />
        </div>
      {:else}
        <ul>
          {#each selectedDomains as domain}
            <li>{domain.label}</li>
          {/each}
        </ul>
      {/if}
    </div>
  {:else if selectedDomains?.length}
    <Results
      bind:data={domainAssociations}
      {onAddUser}
      {onAddDomain}
      {selectedDomains}
      {users}
      bind:loading={checkingDomains}
    />
  {/if}

  <p>{$format('label.ACTION')}</p>
  <Select
    items={actionItems}
    searchable={false}
    bind:value={actionItem}
    placeholder={$format('label.PLACEHOLDER.SELECT_ACTION')}
  />

  <p>{$format('label.AUTH_CODE')}</p>
  <Input type="password" name="authcode" bind:value={authcode} placeholder={$format('label.PLACEHOLDER.AUTH_CODE')} />
</div>

<style lang="scss" src="./mdm-form.scss"></style>
